import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';

interface CreateWorkspaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (workspaceId: string) => void;
}

interface Organization {
  id: string;
  name: string;
}

export const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('trial');
  const [seedCredits, setSeedCredits] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  // Fetch available organizations
  const { data: organizations = [], isLoading: orgsLoading } = useQuery({
    queryKey: ['user-organizations'],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('orgs-manage', {
        body: { action: 'list_orgs' }
      });
      if (error) throw error;
      return data.organizations as Organization[];
    },
    enabled: isOpen,
  });

  // Fetch available plans
  const { data: plans = [] } = useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const { data, error } = await supabase.from('plans').select('*').order('name');
      if (error) throw error;
      return data;
    },
    enabled: isOpen,
  });

  const handleCreate = async () => {
    if (!name.trim() || !selectedOrgId) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke('workspaces-manage', {
        body: {
          action: 'create_workspace',
          org_id: selectedOrgId,
          name: name.trim(),
          plan_code: selectedPlan,
          seed_credits: seedCredits
        }
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Workspace created successfully",
      });

      onSuccess(data.workspace_id);
      handleClose();
    } catch (error: any) {
      console.error('Create workspace error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create workspace",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setName('');
    setSelectedOrgId('');
    setSelectedPlan('trial');
    setSeedCredits(true);
    setIsCreating(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Create Workspace</CardTitle>
            <CardDescription>
              Create a new workspace within your organization
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isCreating}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="workspace-name">Workspace Name</Label>
            <Input
              id="workspace-name"
              placeholder="Enter workspace name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isCreating}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="organization">Organization</Label>
            <Select
              value={selectedOrgId}
              onValueChange={setSelectedOrgId}
              disabled={orgsLoading || isCreating}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select organization" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="plan">Plan</Label>
            <Select
              value={selectedPlan}
              onValueChange={setSelectedPlan}
              disabled={isCreating}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select plan" />
              </SelectTrigger>
              <SelectContent>
                {plans.map((plan) => (
                  <SelectItem key={plan.id} value={plan.code}>
                    {plan.name} {plan.code === 'trial' && '(14-day trial)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="seed-credits"
              className="h-4 w-4 rounded border-gray-300"
              checked={seedCredits}
              onChange={(e) => setSeedCredits(e.target.checked)}
              disabled={isCreating}
            />
            <Label htmlFor="seed-credits" className="text-sm">
              Seed with 100 credits
            </Label>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={handleClose} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isCreating || !name.trim() || !selectedOrgId}>
              {isCreating ? "Creating..." : "Create Workspace"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};